'use client';

import { useClientTranslation } from '@lib/i18n/client';
import { Form } from '@modules/prism-client/components/elements/Form';
import { invokePrismAction } from '@modules/prism-client/lib/invoke-prism-action';
import { ADD_TRIPS_RATE_LIMIT_NAME } from '@tuic/api-kit';
import { useRateLimit } from '@tuic/api-kit/hooks';
import { logger } from '@tuic/logger';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { useToasts } from '@tuic/web-compositions/hooks/use-toasts';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { BookingAlreadyLinkedDialog } from './BookingAlreadyLinkedDialog';

import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { RateLimitHookReturnValue } from '@tuic/api-kit/hooks';
import type { FC, FormEvent } from 'react';

/** Mirrors `BookingAlreadyLinkedError.getType()` from the PRISM add-booking action. */
const BOOKING_ALREADY_LINKED_ERROR = 'BookingAlreadyLinkedError';

type AddBookingFormProps = {
  form: PrismAPI.Form;
  onSuccess: () => void;
};

export const AddBookingForm: FC<AddBookingFormProps> = ({
  form,
  onSuccess,
}) => {
  const { t, i18n } = useClientTranslation();
  const { refresh } = useRouter();
  const { push: pushToast } = useToasts();
  const rateLimit = useRateLimit(ADD_TRIPS_RATE_LIMIT_NAME);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [linkedAccountEmail, setLinkedAccountEmail] = useState<string>();

  const defaultErrorMessage = t(
    'Leider haben wir aktuell technische Probleme. Bitte versuchen Sie es in wenigen Minuten erneut.',
  );

  const handleSubmit = async (
    ev: FormEvent<HTMLFormElement>,
    callbacks: any,
  ) => {
    ev.preventDefault();
    setErrorMessage(undefined);
    setFieldErrors({});

    try {
      const formData = new FormData(ev.target as HTMLFormElement);
      const formParams = Object.fromEntries(formData);

      const result = await invokePrismAction<PrismAPI.AddBookingAction>(
        form.action,
        formParams,
        i18n.resolvedLanguage,
      );

      if (result.success) {
        pushToast({
          title: t('Glückwunsch!'),
          text:
            result.data?.message ??
            t('Sie haben Ihre Reise erfolgreich hinzugefügt.'),
        });

        onSuccess();
        refresh();
        return;
      }

      if (result.error?.name === BOOKING_ALREADY_LINKED_ERROR) {
        const maskedEmail = result.error.details?.maskedEmail;
        if (maskedEmail) {
          setLinkedAccountEmail(maskedEmail);
        } else {
          setErrorMessage(result.error.message ?? defaultErrorMessage);
        }
        return;
      }

      if (result.error?.details?.fields) {
        const errors: Record<string, string> = {};
        for (const fieldError of result.error.details.fields) {
          errors[fieldError.name] = fieldError.message;
        }
        setFieldErrors(errors);
        return;
      }

      setErrorMessage(result.error?.message ?? defaultErrorMessage);
    } catch (error) {
      logger.error('Failed to add booking via PRISM action:', error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      <Form
        data={getFormData(form, rateLimit)}
        fieldErrors={fieldErrors}
        onSubmit={handleSubmit}
        meta={renderAlert(t, rateLimit, errorMessage)}
        footNote={
          <p className="text-sm text-marine-high-emphasis">
            * {t('Pflichtfeld')}
          </p>
        }
      />
      <BookingAlreadyLinkedDialog
        open={linkedAccountEmail !== undefined}
        onOpenChange={open => {
          if (!open) setLinkedAccountEmail(undefined);
        }}
        maskedEmail={linkedAccountEmail}
      />
    </>
  );
};

const renderAlert = (
  t: ReturnType<typeof useClientTranslation>['t'],
  rateLimit: ReturnType<typeof useRateLimit>,
  errorMessage: string | undefined,
) => {
  const message = rateLimit.limited
    ? t(
        'Aufgrund zu vieler Fehlversuche, haben wir die Eingabe aus Sicherheitsgründen noch für {{ time }} gesperrt. Bei weiteren Fragen wenden Sie sich bitte an das Serviceteam.',
        { time: rateLimit.retryInExpression },
      )
    : errorMessage;

  return message ? <Alert mode="error">{message}</Alert> : null;
};

const getFormData = (
  data: PrismAPI.Form,
  rateLimit: RateLimitHookReturnValue,
): PrismAPI.Form => {
  if (!rateLimit.limited) {
    return data;
  }

  return {
    ...data,
    fields: [],
    submitButton: {
      ...data.submitButton,
      disabled: true,
    },
    cancelButton: data.cancelButton
      ? {
          ...data.cancelButton,
          disabled: true,
        }
      : undefined,
  };
};
